// First Created - 06 May 2026
// Author - Atul Pandey

import {
    createSelector,
    createEntityAdapter,
} from '@reduxjs/toolkit'
import { apiSlice } from '@/app/api/apiSlice'

const userSchedulingPagesAdapter = createEntityAdapter({})

const initialState = userSchedulingPagesAdapter.getInitialState()

export const userSchedulingPagesApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getUserSchedulingPages: builder.query({
            query: () => ({
                url: '/userSchedulingPages/getUserSchedulingPages',
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                }
            }),
            transformResponse: responseData => {
                const loadedUserSchedulingPages = responseData.map(userSchedulingPage => {
                    userSchedulingPage.id = userSchedulingPage._id
                    return userSchedulingPage
                })
                return userSchedulingPagesAdapter.setAll(initialState, loadedUserSchedulingPages)
            },
            providesTags: (result, error, arg) => {
                if (result?.ids) {
                    return [
                        { type: 'UserSchedulingPage', id: 'LIST' },
                        ...result.ids.map(id => ({ type: 'UserSchedulingPage', id }))
                    ]
                } else {
                    return [{ type: 'UserSchedulingPage', id: 'LIST' }]
                }
            },
        }),

        getUserSchedulingPageById: builder.query({
            query: ({ schedulingPageId }) => ({
                url: '/userSchedulingPages/getUserSchedulingPageById',
                params: { schedulingPageId },
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                }
            }),
            providesTags: (result, error, arg) => [
                { type: 'UserSchedulingPage', id: arg?.schedulingPageId }
            ],
        }),

        createNewUserSchedulingPage: builder.mutation({
            query: initialUserSchedulingPage => ({
                url: '/userSchedulingPages/createNewUserSchedulingPage',
                method: 'POST',
                body: { ...initialUserSchedulingPage }
            }),
            invalidatesTags: [
                { type: 'UserSchedulingPage', id: 'LIST' }
            ]
        }),

        updateUserSchedulingPage: builder.mutation({
            query: initialUserSchedulingPage => ({
                url: '/userSchedulingPages/updateUserSchedulingPage',
                method: 'PATCH',
                body: { ...initialUserSchedulingPage }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'UserSchedulingPage', id: arg.schedulingPageId },
                { type: 'UserSchedulingPage', id: 'LIST' }
            ],
        }),

        deleteUserSchedulingPage: builder.mutation({
            query: ({ schedulingPageId }) => ({
                url: '/userSchedulingPages/deleteUserSchedulingPage',
                method: 'DELETE',
                body: { schedulingPageId }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'UserSchedulingPage', id: arg.schedulingPageId },
                { type: 'UserSchedulingPage', id: 'LIST' }
            ]
        }),

        verifySchedulingPagePasscode: builder.mutation({
            query: initialVerification => ({
                url: '/userSchedulingPages/verifySchedulingPagePasscode',
                method: 'POST',
                body: { ...initialVerification }
            }),
        }),

        getPublicSchedulingSlots: builder.query({
            query: ({ publicSlug }) => ({
                url: '/userSchedulingPages/getPublicSchedulingSlots',
                params: { publicSlug },
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                }
            }),
        }),

        bookPublicSchedulingSlot: builder.mutation({
            query: initialBooking => ({
                url: '/userSchedulingPages/bookPublicSchedulingSlot',
                method: 'PATCH',
                body: { ...initialBooking }
            }),
        }),
    }),
})

export const {
    useGetUserSchedulingPagesQuery,
    useGetUserSchedulingPageByIdQuery,
    useCreateNewUserSchedulingPageMutation,
    useUpdateUserSchedulingPageMutation,
    useDeleteUserSchedulingPageMutation,
    useVerifySchedulingPagePasscodeMutation,
    useGetPublicSchedulingSlotsQuery,
    useBookPublicSchedulingSlotMutation,
} = userSchedulingPagesApiSlice

export const selectUserSchedulingPageResult =
    userSchedulingPagesApiSlice.endpoints.getUserSchedulingPages.select()

const selectUserSchedulingPagesData = createSelector(
    selectUserSchedulingPageResult,
    userSchedulingPagesResult => userSchedulingPagesResult.data
)

export const {
    selectAll: selectAllUserSchedulingPages,
    selectById: selectUserSchedulingPageById,
    selectIds: selectUserSchedulingPageIds,
} = userSchedulingPagesAdapter.getSelectors(
    state => selectUserSchedulingPagesData(state) ?? initialState
)